import { wrapOnClick } from '@/utils/clickEvent';
import { Component, ReturnComponent, Style } from '@/utils/reactTypes';
import { labelToTestId } from '@/utils/testIds';
import { ListItemIcon, ListItemText, Menu, MenuProps, Tooltip } from '@mui/material';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import { type MouseEvent } from 'react';
import { Link } from 'react-router';

export type MenuItemInfo = {
    condition?: boolean;
    icon?: Component;
    label: string;
    tooltip?: string;
    onClick(event: MouseEvent<HTMLElement>): void;
    style?: Style;
    testId?: string;
    to?: string;
} & Omit<MenuItemProps, 'onClick'>;

export const AxleMenu = ({
    isOpen,
    close,
    anchorEl,
    items,
    testId: menuTestId,
    ...rest
}: {
    isOpen: boolean;
    close: () => void;
    items: MenuItemInfo[];
    testId?: string;
} & Omit<MenuProps, 'open' | 'onClose'>): ReturnComponent => {
    const menuItems = [];
    for (const {
        condition = true,
        icon,
        label,
        tooltip = '',
        onClick,
        style,
        testId: itemTestId,
        to,
        ...rest
    } of items) {
        const _itemTestId =
            itemTestId ??
            (typeof menuTestId === 'string' ? `${menuTestId}-${labelToTestId(label)}` : undefined);
        if (condition) {
            menuItems.push(
                <Tooltip key={label} title={tooltip} placement={'left'}>
                    <MenuItem
                        {...rest}
                        {...(to !== undefined && { component: Link, to })}
                        data-test-id={_itemTestId}
                        onClick={wrapOnClick(onClick)}
                        style={{ ...style, display: 'flex', alignItems: 'center' }}
                    >
                        {icon && <ListItemIcon>{icon}</ListItemIcon>}
                        <ListItemText>{label}</ListItemText>
                    </MenuItem>
                </Tooltip>,
            );
        }
    }

    return (
        <Menu
            {...rest}
            open={isOpen}
            onClose={close}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            data-test-id={menuTestId ? `${menuTestId}-root` : undefined}
            // TODO(jessica): One we upgrade react and MUI v6, add a specific test ID to the list element
        >
            {menuItems}
        </Menu>
    );
};
